import React, { useEffect, useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { useNavigate } from 'react-router-dom';
import { getKbDataChartMulti } from '../lib/api/kb-api';
import './KbTrendComboChart.css';

const DEFAULT_REGION_ID = 195;

const VIEW_OPTIONS = [
  { value: 'SALE', label: '주간 아파트 매매가격지수' },
  { value: 'JEONSE', label: '주간 아파트 전세가격지수' },
];

const formatDateTick = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const yy = String(date.getFullYear()).slice(2);
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `'${yy}.${mm}.${dd}`;
};

const formatDateOnly = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

function KbTrendComboChart({ regionId = DEFAULT_REGION_ID, titleRegionName }) {
  const navigate = useNavigate();
  const [viewType, setViewType] = useState('SALE');
  const [seriesRows, setSeriesRows] = useState([]);
  const [displayRegionName, setDisplayRegionName] = useState('전국');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const selectedRegionId = Number(regionId) || DEFAULT_REGION_ID;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const indexTypes = viewType === 'SALE' ? ['SALE', 'SALE_IDX'] : ['JEONSE', 'JEONSE_IDX'];
        const result = await getKbDataChartMulti({
          regionId: selectedRegionId,
          indexTypes,
        });
        const rows = Array.isArray(result) ? result : [];

        const validDates = rows
          .map((row) => new Date(row?.baseDate || ''))
          .filter((date) => !Number.isNaN(date.getTime()));

        if (validDates.length === 0) {
          setSeriesRows([]);
          return;
        }

        const latestBaseDate = new Date(Math.max(...validDates.map((d) => d.getTime())));
        const startBaseDate = new Date(latestBaseDate);
        startBaseDate.setFullYear(startBaseDate.getFullYear() - 2);

        const filtered = rows.filter((row) => {
          const d = new Date(row?.baseDate || '');
          if (Number.isNaN(d.getTime())) return false;
          return d >= startBaseDate && d <= latestBaseDate;
        });

        setSeriesRows(filtered);
        const firstNamed = filtered.find((row) => row?.regionNameKo)?.regionNameKo;
        setDisplayRegionName(firstNamed || '전국');
      } catch (e) {
        setSeriesRows([]);
        setError(e?.message || '지수/변동률 차트 데이터를 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [viewType, selectedRegionId]);

  const chartOption = useMemo(() => {
    const dateMap = new Map();
    let regionName = '서울';

    seriesRows.forEach((row) => {
      const baseDate = row?.baseDate;
      const indexType = String(row?.indexType || '');
      const value = Number(row?.value || 0);
      if (!baseDate) return;

      if (row?.regionNameKo) {
        regionName = row.regionNameKo;
      }

      if (!dateMap.has(baseDate)) {
        dateMap.set(baseDate, {
          date: baseDate,
          change: null,
          idx: null,
        });
      }

      const current = dateMap.get(baseDate);
      if (indexType.endsWith('_IDX')) {
        current.idx = value;
      } else {
        current.change = value;
      }
    });

    const timeline = Array.from(dateMap.values()).sort((a, b) => new Date(a.date) - new Date(b.date));
    const dates = timeline.map((item) => item.date);
    const idxValues = timeline.map((item) => (item.idx == null ? null : Number(item.idx)));
    const riseBars = timeline.map((item) => (item.change != null && item.change >= 0 ? Number(item.change) : null));
    const fallBars = timeline.map((item) => (item.change != null && item.change < 0 ? Number(item.change) : null));
    const validIdxValues = idxValues.filter((v) => Number.isFinite(v));
    const minIdx = validIdxValues.length ? Math.min(...validIdxValues) : 0;
    const maxIdx = validIdxValues.length ? Math.max(...validIdxValues) : 0;
    const idxPad = Math.max((maxIdx - minIdx) * 0.18, 0.8);

    const validChanges = timeline
      .map((item) => Number(item.change))
      .filter((v) => Number.isFinite(v));
    const maxAbsChange = validChanges.length ? Math.max(...validChanges.map((v) => Math.abs(v))) : 0.1;
    const changeAxisMax = Number((Math.max(maxAbsChange * 1.2, 0.05)).toFixed(2));

    return {
      animationDuration: 500,
      grid: {
        left: 54,
        right: 50,
        top: 52,
        bottom: 36,
        containLabel: true,
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params = []) => {
          if (!Array.isArray(params) || params.length === 0) {
            return '';
          }

          const dateText = formatDateOnly(params[0]?.axisValue);
          const idxParam = params.find((p) => p?.seriesName === regionName);
          const riseParam = params.find((p) => p?.seriesName === `${regionName}(상승)`);
          const fallParam = params.find((p) => p?.seriesName === `${regionName}(하락)`);

          const idxValue = idxParam?.data;
          const riseValue = riseParam?.data;
          const fallValue = fallParam?.data;
          const changeValue = riseValue ?? fallValue;

          const idxText = idxValue == null ? '-' : Number(idxValue).toFixed(2).replace(/\.?0+$/, '');
          const changeText = changeValue == null ? '-' : Number(changeValue).toFixed(2).replace(/\.?0+$/, '');

          return [
            `<div style="font-weight:700;margin-bottom:4px;">${dateText}</div>`,
            `<div>${regionName} 지수: <strong>${idxText}</strong></div>`,
            `<div>${regionName} 변동율(%): <strong>${changeText}</strong></div>`,
          ].join('');
        },
      },
      xAxis: {
        type: 'category',
        data: dates,
        axisLabel: {
          color: '#52627d',
          fontSize: 11,
          formatter: formatDateTick,
          hideOverlap: true,
        },
        axisTick: { show: false },
        axisLine: { lineStyle: { color: '#dce4ee' } },
      },
      yAxis: [
        {
          type: 'value',
          name: '지수',
          scale: true,
          min: Number((minIdx - idxPad).toFixed(2)),
          max: Number((maxIdx + idxPad).toFixed(2)),
          nameTextStyle: {
            color: '#334155',
            fontWeight: 700,
            fontSize: 11,
          },
          axisLabel: { color: '#334155', fontSize: 11 },
          splitLine: {
            lineStyle: { color: '#e8edf4' },
          },
        },
        {
          type: 'value',
          name: '(%)',
          position: 'right',
          min: -changeAxisMax,
          max: changeAxisMax,
          nameTextStyle: {
            color: '#334155',
            fontWeight: 700,
            fontSize: 11,
          },
          axisLabel: {
            color: '#334155',
            fontSize: 11,
            formatter: (value) => Number(value).toFixed(2).replace(/\.?0+$/, ''),
          },
          splitLine: { show: false },
        },
      ],
      series: [
        {
          name: `${regionName}(상승)`,
          type: 'bar',
          yAxisIndex: 1,
          data: riseBars,
          barWidth: 6,
          barCategoryGap: '42%',
          itemStyle: {
            color: '#ec8b86',
            borderColor: '#ffffff',
            borderWidth: 1,
          },
        },
        {
          name: `${regionName}(하락)`,
          type: 'bar',
          yAxisIndex: 1,
          data: fallBars,
          barWidth: 6,
          itemStyle: {
            color: '#6aa3f4',
            borderColor: '#ffffff',
            borderWidth: 1,
          },
        },
        {
          name: regionName,
          type: 'line',
          smooth: true,
          symbol: 'none',
          yAxisIndex: 0,
          data: idxValues,
          lineStyle: {
            width: 2,
            color: '#df5f2f',
          },
        },
      ],
    };
  }, [seriesRows]);

  const viewOptions = useMemo(() => {
    const cleanRegionName = String(titleRegionName || '').trim();
    if (!cleanRegionName) {
      return VIEW_OPTIONS;
    }

    return [
      { value: 'SALE', label: `주간 ${cleanRegionName} 아파트 매매가격지수` },
      { value: 'JEONSE', label: `주간 ${cleanRegionName} 아파트 전세가격지수` },
    ];
  }, [titleRegionName]);

  const activeViewLabel = useMemo(() => {
    const found = viewOptions.find((option) => option.value === viewType);
    return found?.label || VIEW_OPTIONS[0].label;
  }, [viewOptions, viewType]);

  return (
    <section className="kb-trend-chart-card">
      <div className="kb-trend-chart-header">
        <div className="kb-trend-chart-title-wrap">
          <div className="kb-trend-chart-title-row">
            <span className="kb-trend-chart-title-icon" aria-hidden="true"><i className="fas fa-chart-line" /></span>
            <h3 className="kb-trend-chart-title-text">{activeViewLabel}</h3>
          </div>
        </div>
        <div className="kb-trend-header-right">
          <div className="kb-trend-view-toggle">
            <button
              type="button"
              className={viewType === 'SALE' ? 'active' : ''}
              onClick={() => setViewType('SALE')}
            >
              매매
            </button>
            <button
              type="button"
              className={viewType === 'JEONSE' ? 'active' : ''}
              onClick={() => setViewType('JEONSE')}
            >
              전세
            </button>
          </div>
        </div>
      </div>


      {error && <p className="kb-trend-chart-error">{error}</p>}
      {!error && loading && <div className="kb-trend-chart-state">차트 데이터를 불러오는 중...</div>}
      {!error && !loading && seriesRows.length === 0 && <div className="kb-trend-chart-state">표시할 데이터가 없습니다.</div>}

      {!error && !loading && seriesRows.length > 0 && (
        <ReactECharts option={chartOption} style={{ width: '100%', height: '360px' }} opts={{ renderer: 'canvas' }} notMerge lazyUpdate />
      )}

      <div className="kb-trend-chart-bottom-meta">
        <div className="kb-trend-chart-bottom-left">
          <div className="kb-trend-legend" aria-label="차트 범례">
            <span className="kb-trend-legend-item"><i className="dot rise" />{displayRegionName}(상승)</span>
            <span className="kb-trend-legend-item"><i className="dot fall" />{displayRegionName}(하락)</span>
            <span className="kb-trend-legend-item"><i className="dot line" />{displayRegionName}</span>
          </div>
          <span className="kb-trend-chart-unit-text">단위: 지수, %</span>
          <span className="kb-trend-chart-source">제공 : KB부동산</span>
        </div>
        <button
          type="button"
          className="kb-trend-bottom-link"
          onClick={() => navigate(`/kb-data?viewMode=table&indexType=${viewType}&autoSearch=1`)}
        >
          자세히 보기 &gt;
        </button>
      </div>
    </section>
  );
}

export default KbTrendComboChart;
